import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback,  useMemo } from 'react';
import { RootState } from '@root/rootReducer';
import { IndexedState, IndexedStates, MainIndex, setCornerstoneInitialize, setFullscreenPanelComponentKey, setSelectedPanelState, updateCornerstoneRenderingEngineId, updateCPRStateBatch, updateCPRStateIndexedState, updateCPRStateIndexedStates, updateCPRStateMainIndex, updateCPRStateMainIndices, updatePanelStateChamberViewProps, updatePanelStateCoronary, updatePanelStateDirection, updatePanelStateImageSize, updatePanelStatePosition, updatePanelStates, updatePanelStateSegmentation, updatePanelStateSliceIndex, updatePanelStateSpacing, updatePanelStateThickness, updatePanelStateThicknessMethod, updatePanelStateZoom, updateSelectedLayoutPanelConfig} from '@features/analysis/AnalysisPageSlice';
import { setSelectedPanelConfig } from '@features/analysis/AnalysisPageSlice';
import { PanelComponentKeyType } from 'config/panelConfig';
import { PanelConfig } from 'config/panelConfig';
import {
  AnalysisType,
  ChamberViewProps,
  DirectionType,
  PanelState,
  PanelStates,
  PresetType,
  Segmentation,
  SegmentationControllerType,
  ThicknessMethodType,
} from 'config/analysisConfig';
import {
  // getInitialPanelStates,
  // getFullscreenPanelComponentKey,
  selectCurrentPanelState,
  // selectCurrentPanelPanelStateEx,
} from '@features/analysis/AnalysisPageSelectors';

export interface PanelContainerHookResult {
  analysisType: AnalysisType;
  selectedPanelComponentKey?: PanelComponentKeyType;
  fullscreenPanelComponentKey?: PanelComponentKeyType;
  selectedPanelClassName : string | undefined | null;
  panelStates: PanelStates;
  selectedPanelState : PanelState;
  isCornerstoneInitialized: boolean;
  getPanelState:(componentKey : PanelComponentKeyType)=>PanelState;
  swapLayoutPanelConfig: (srcProjection : PanelComponentKeyType,destProjection : PanelComponentKeyType)=>void;
  updateSelectedPanelState: (panelState: PanelState)=>void;
  updatePanelState: (componentKey : PanelComponentKeyType | SegmentationControllerType, key: keyof PanelState | string, value: PanelState[keyof PanelState])=>void;
  updateCPRState:(params: {
    mainIndex?: MainIndex,
    mainIndices?: [number,number,number];
    indexedState? : IndexedState,
    indexedStates? : IndexedStates
    })=>void;
  updateFullscreenPanelComponentKey : (componentKey?: PanelComponentKeyType)=>void;
  updateSelectedPanelConfig: (panelConfig: PanelConfig) => void;
  setCornerstoneInitializedFlag: (flag : boolean)=>void;
  updateRenderingEngineId: (renderingEngineId : string)=>void;
  resetPanelStates : (panelStates : Partial<PanelStates>)=>void;
  cornerstoneEngineIDs : string[];
}

export const usePanelContainer = (): PanelContainerHookResult => {
  const dispatch = useDispatch();

  const { analysisType, selectedPanelComponentKey, selectedPanelClassName } = useSelector(
    (state: RootState) => ({
      analysisType: state.analysis.selectedAnalysis?.analysisType || 'empty',
      selectedPanelComponentKey:state.analysis.uiConfig.layoutState.selectedPanelConfig?.componentKey,
      selectedPanelClassName: state.analysis.uiConfig.layoutState.selectedPanelConfig?.className
    }),
    shallowEqual,
  );
  const fullscreenPanelComponentKey = useSelector((state: RootState) =>
    state.analysis.uiConfig.layoutState.fullscreenPanelComponentKey,shallowEqual);

  const panelStates = useSelector((state: RootState) => state.analysis.panelStates, shallowEqual);
  const selectedPanelState = useSelector(selectCurrentPanelState,shallowEqual)

  const isCornerstoneInitialized = useSelector((state: RootState)=>
    state.analysis.cornerstoneConfig.initialized,shallowEqual);

  const cornerstoneEngineIDs = useSelector((state : RootState)=>
    state.analysis.cornerstoneConfig.renderingEngineId);


  const getPanelState = useCallback((componentKey : PanelComponentKeyType)=>
    panelStates[componentKey]
  ,[panelStates,dispatch])


  const swapLayoutPanelConfig = useCallback((srcProjection : PanelComponentKeyType,destProjection : PanelComponentKeyType)=>{
    dispatch(updateSelectedLayoutPanelConfig([srcProjection,destProjection]));
  },[dispatch])

  const updateSelectedPanelState = useCallback((panelState : PanelState)=>{
    dispatch(setSelectedPanelState(panelState));
  },[dispatch])

  const updatePanelState = useCallback((componentKey : PanelComponentKeyType | SegmentationControllerType, key: keyof PanelState | string, value: PanelState[keyof PanelState])=>{
    switch (key) {
      case 'thickness':
        dispatch(updatePanelStateThickness([componentKey as PanelComponentKeyType,value as number]));
        break;
      case 'thicknessMethod':
        dispatch(updatePanelStateThicknessMethod([componentKey as PanelComponentKeyType,value as ThicknessMethodType]));
        break;
      case 'zoom':
        dispatch(updatePanelStateZoom([componentKey as PanelComponentKeyType,value as number]));
        break;
      // case 'windowLevel':
      //   dispatch(updatePanelStateWindowLevel([componentKey as PanelComponentKeyType,value as number]));
      //   break;
      // case 'windowWidth':
      //   dispatch(updatePanelStateWindowWidth([componentKey as PanelComponentKeyType,value as number]));
      //   break;
      // case 'windowLevelWidth':
      //   const {levelWidth} = value as Record<string,PresetType>
      //   dispatch(updatePanelStateWindowLevelWidth([componentKey as PanelComponentKeyType,levelWidth.windowLevel,levelWidth.windowWidth]));
      //   break;
      case 'spacing':
        dispatch(updatePanelStateSpacing([componentKey as PanelComponentKeyType,value as number]));
        break;
      case 'imageSize':
        dispatch(updatePanelStateImageSize([componentKey as PanelComponentKeyType,value as number]));
        break;
      case 'sliceIndex':
        dispatch(updatePanelStateSliceIndex([componentKey as PanelComponentKeyType,value as number]));
        break;
      case 'position':
        dispatch(updatePanelStatePosition([componentKey as PanelComponentKeyType, value as [number, number, number]]));
        break;
      // case 'preset':
      //   dispatch(updatePanelStatePreset([componentKey as PanelComponentKeyType,value as Record<string,PresetType>]));
      //   break;
      case 'segmentation':
        const segmentationType = componentKey as SegmentationControllerType
        const segmentationProps = (value as Segmentation)[segmentationType];
        dispatch(updatePanelStateSegmentation([segmentationType,segmentationProps]));
        break;
      case 'direction':
        dispatch(updatePanelStateDirection([componentKey as PanelComponentKeyType, value as DirectionType]))
        break;
      case 'chamberViewProps':
        dispatch(updatePanelStateChamberViewProps([componentKey as PanelComponentKeyType, value as ChamberViewProps]))
        break;
      // case 'coronary':
      //   dispatch(updatePanelStateCoronary([componentKey as PanelComponentKeyType, ]))
      //   break;
      default:
        console.warn('Invalid PanelState Type');
        break;
    }
  },[dispatch]);
  const updateCPRState = useCallback((
    params: {
    mainIndex?: MainIndex,
    mainIndices?: [number,number,number];
    indexedState? : IndexedState,
    indexedStates? : IndexedStates
  })=>{
    const {mainIndex, mainIndices, indexedState,indexedStates} = params;
    if ((mainIndex || mainIndices) && (indexedState || indexedStates)) {
      dispatch(updateCPRStateBatch(params));
      return;
    }
    if(mainIndex){
      dispatch(updateCPRStateMainIndex(mainIndex));
    }
    if(mainIndices){
      dispatch(updateCPRStateMainIndices(mainIndices));
    }
    if(indexedState){
      dispatch(updateCPRStateIndexedState(indexedState));
    }
    if(indexedStates){
      dispatch(updateCPRStateIndexedStates(indexedStates));
    }

  },[dispatch])
  // const updatePanelStateThickness = useCallback((componentKey : PanelComponentKeyType, panelState : PanelState)=>{
  //   dispatch(updatePanelStates({[componentKey]:panelState}));
  // },[dispatch,panelStates]);
  // const updatePanelStateThicknessMethod = useCallback((componentKey : PanelComponentKeyType, panelState : PanelState)=>{
  //   dispatch(updatePanelStates({[componentKey]:panelState}));
  // },[dispatch,panelStates]);
  // const updatePanelStateZoom = useCallback((componentKey : PanelComponentKeyType, panelState : PanelState)=>{
  //   dispatch(updatePanelStates({[componentKey]:panelState}));
  // },[dispatch,panelStates]);
  // const updatePanelStateWindowWidth = useCallback((componentKey : PanelComponentKeyType, panelState : PanelState)=>{
  //   dispatch(updatePanelStates({[componentKey]:panelState}));
  // },[dispatch,panelStates]);
  // const updatePanelStateWindowLevel = useCallback((componentKey : PanelComponentKeyType, panelState : PanelState)=>{
  //   dispatch(updatePanelStates({[componentKey]:panelState}));
  // },[dispatch,panelStates]);

  const resetPanelStates=useCallback  ((panelStates : Partial<PanelStates>)=>{
    dispatch(updatePanelStates(panelStates));
  },[dispatch])

  const updateFullscreenPanelComponentKey = useCallback((componentKey?: PanelComponentKeyType)=>{
    dispatch  (setFullscreenPanelComponentKey(componentKey ?? "Empty"));
  },[dispatch])

  const updateSelectedPanelConfig = useCallback((panelConfig: PanelConfig) => {
    dispatch(setSelectedPanelConfig(panelConfig));
  },[dispatch]);

  const setCornerstoneInitializedFlag = useCallback((flag : boolean)=>{
    dispatch(setCornerstoneInitialize(flag));
  },[dispatch]);

  const updateRenderingEngineId = useCallback((renderingEngineId : string)=>{
    dispatch(updateCornerstoneRenderingEngineId(renderingEngineId));
  },[dispatch]);

  return useMemo(
    () => ({
      analysisType,
      selectedPanelComponentKey,
      selectedPanelClassName,
      fullscreenPanelComponentKey,
      selectedPanelState,
      panelStates,
      isCornerstoneInitialized,
      // topLeftContent,
      // topRightContent,
      getPanelState,
      swapLayoutPanelConfig,
      updateSelectedPanelState,
      updatePanelState,
      updateCPRState,
      updateFullscreenPanelComponentKey,
      updateSelectedPanelConfig,
      setCornerstoneInitializedFlag,
      updateRenderingEngineId,
      resetPanelStates,
      cornerstoneEngineIDs,
    }),
    [
      analysisType,
      selectedPanelComponentKey,
      selectedPanelClassName,
      fullscreenPanelComponentKey,
      selectedPanelState,
      panelStates,
      isCornerstoneInitialized,
      // initialPanelStates,
      // topLeftContent,
      // topRightContent,
      getPanelState,
      swapLayoutPanelConfig,
      updateSelectedPanelState,
      updatePanelState,
      updateCPRState,
      updateFullscreenPanelComponentKey,
      updateSelectedPanelConfig,
      setCornerstoneInitializedFlag,
      updateRenderingEngineId,
      resetPanelStates,
      cornerstoneEngineIDs
    ],
  );
};
